import { useState, type FormEvent } from "react";
import { useNotify, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { getSupabaseClient } from "../providers/supabase/supabase";

/**
 * AEGYRA : connexion par code à six chiffres envoyé par e-mail. L'e-mail ne porte aucun lien, qu'un
 * antivirus de messagerie pourrait ouvrir et consommer avant l'utilisateur. Aucun compte n'est créé :
 * seuls les comptes déjà invités reçoivent un code.
 */
export const CodeLoginForm = ({ redirectTo }: { redirectTo?: string }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const sendCode = async (event?: FormEvent) => {
    event?.preventDefault();
    setLoading(true);
    const { error } = await getSupabaseClient().auth.signInWithOtp({
      email: email.trim(),
      options: { shouldCreateUser: false },
    });
    setLoading(false);
    // Même message qu'un compte existe ou non, pour ne pas révéler les adresses inscrites ;
    // seule une limite d'envoi atteinte est signalée.
    if (error && error.status === 429) {
      notify("crm.auth.code.too_many", { type: "error" });
      return;
    }
    setSent(true);
    notify("crm.auth.code.sent", { type: "info" });
  };

  const verify = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    const token = code.replace(/\D/g, "");
    const { error } = await getSupabaseClient().auth.verifyOtp({
      email: email.trim(),
      token,
      type: "email",
    });
    if (error) {
      setLoading(false);
      notify("crm.auth.code.invalid", { type: "error" });
      return;
    }
    // Rechargement complet : l'application relit la session et les droits de l'utilisateur. Changer
    // seulement le fragment ne recharge pas la page, et une requête lancée sans session depuis l'écran de
    // connexion (refusée en 401) déconnectait aussitôt l'utilisateur.
    window.history.replaceState(null, "", `./#${redirectTo ?? "/"}`);
    window.location.reload();
  };

  if (!sent) {
    return (
      <form className="space-y-6" onSubmit={sendCode}>
        <div className="space-y-2">
          <Label htmlFor="login-email">{translate("ra.auth.email")}</Label>
          <Input
            id="login-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <Button
          type="submit"
          className="w-full cursor-pointer"
          disabled={loading}
        >
          {translate("crm.auth.code.send")}
        </Button>
      </form>
    );
  }

  return (
    <form className="space-y-6" onSubmit={verify}>
      <p className="text-sm text-muted-foreground">
        {translate("crm.auth.code.help", { email: email.trim() })}
      </p>
      <div className="space-y-2">
        <Label htmlFor="login-code">{translate("crm.auth.code.label")}</Label>
        <Input
          id="login-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9 ]*"
          maxLength={8}
          required
          autoFocus
          className="text-center text-2xl tracking-[0.5em] font-mono"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
      </div>
      <Button
        type="submit"
        className="w-full cursor-pointer"
        disabled={loading}
      >
        {translate("ra.auth.sign_in")}
      </Button>
      <div className="flex justify-between text-sm">
        <button
          type="button"
          className="hover:underline cursor-pointer"
          onClick={() => {
            setSent(false);
            setCode("");
          }}
        >
          {translate("crm.auth.code.change_email")}
        </button>
        <button
          type="button"
          className="hover:underline cursor-pointer"
          disabled={loading}
          onClick={() => sendCode()}
        >
          {translate("crm.auth.code.resend")}
        </button>
      </div>
    </form>
  );
};
